import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import {
    Card,
    CardBody,
    Col,
    Container,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    NavItem,
    Row,
    TabContent,
    TabPane,
    UncontrolledDropdown,
} from 'reactstrap'
import { PermissionsProps, RouteParams } from '../../../api/globalTypes'
import images from '../../../api/images/hooks'
import { Image } from '../../../api/images/types'
import properties from '../../../api/properties/hooks'
import { PropertyWebsite } from '../../../api/properties/types/response'
import { APPLICATION_SETTINGS, MODAL_TYPES, PROPERTY_STATUS, WEBSITE_ATTRIBUTES } from '../../../common/constants'
import AppPermissionsSwitcher from '../../../Components/AppPermissionsSwitcher/AppPermissionsSwitcher'
import { action } from '../../../Components/AppPermissionsSwitcher/constants/actions'
import { EntityParamsProvider } from '../../../Components/AppPermissionsSwitcher/EntityParamsContext'
import CollapseCard from '../../../Components/Common/CollapseCard'
import FormTabNavigation from '../../../Components/Common/FormTabNavigation'
import Loader from '../../../Components/Common/Loader'
import NavigationActions from '../../../Components/Common/NavigationActions/NavigationActions'
import ViewDataCard from '../../../Components/Common/ViewDataCard'
import ViewDataList from '../../../Components/Common/ViewDataList'
import { DocumentTable } from '../../../Components/Documents'
import MapContainer from '../../../Components/MapContainer'
import { DeleteModalProps, setupDeleteCallback } from '../../../Components/Modals/DeleteModal/utils'
import NotesViewAccordion from '../../../Components/NotesViewAccordion'
import QueryErrorBoundary from '../../../Components/QueryErrorBoundary/QueryErrorBoundary'
import Slider from '../../../Components/Slider/Slider'
import SliderOptions from '../../../Components/Slider/SliderOptions'
import { useColumnOrder } from '../../../Components/TanstackTable'
import { showModal } from '../../../store/modal'
import { selectSearchCriteriaProperties } from '../../../store/search'
import { coerceLatLng, embedYotubeVideo, isApproxLocationSyncType } from '../../../utils/general'
import { propertyViewTabs } from '../constants'
import { usePropertyGeneralViewData } from '../hooks'
import HeaderPropertyView from './HeaderPropertyView/HeaderPropertyView'
import OpportunityList from './OpportunityList/OpportunityList'
import { ViewData } from './PropertyView.types'
import { getModalPayloads } from './viewModalPayloads'

const PropertyView = () => {
    const { id } = useParams<RouteParams>()

    return (
        <div className="page-content">
            <Container fluid>
                <QueryErrorBoundary resetKeys={[id]}>
                    <PropertyViewContent id={id} />
                </QueryErrorBoundary>
            </Container>
        </div>
    )
}

export default PropertyView

// Owns the query that throws on error, and everything downstream of it
const PropertyViewContent = ({ id }: { id: string }) => {
    const history = useHistory()
    const location = useLocation()
    const dispatch = useDispatch()
    const { t } = useTranslation()

    const { DELETE_MODAL, OFFER_BASE_MODAL, TEMPLATES_MODAL, RETURN_TO_OFFER_MODAL, CLONE_MODAL, ACTIVITY_MODAL, AGENT_PRINT_MODAL } = MODAL_TYPES

    const currentLang = localStorage.getItem('i18nextLng') ?? 'hr'

    const criteria = useSelector(selectSearchCriteriaProperties)
    const order = useColumnOrder('property')
    const params = { criteria, order }

    const { isLoading: fetchingProperty, data: propertyData } = properties.GetProperty.useQuery(id, params, { throwOnError: true })
    const { mutate: deleteProperty } = properties.DeleteProperty.useMutation()

    const { isLoading: fetchingPhotos, data: photosData } = images.GetImages.useQuery({ module: 'property', parent: id, collection: 'photo' })
    const { isLoading: fetchingGroundPlans, data: groundPlansData } = images.GetImages.useQuery({
        module: 'property',
        parent: id,
        collection: 'ground_plan',
    })

    const property = propertyData?.item ?? null
    const photos: Image[] | undefined = !fetchingPhotos ? photosData : []
    const groundPlans = !fetchingGroundPlans ? groundPlansData : []

    const currency = APPLICATION_SETTINGS.CURRENCY
    const areaUnit = APPLICATION_SETTINGS.AREA_UNIT

    const viewData: ViewData = usePropertyGeneralViewData(property?.propertyType?.id, property?.propertyService?.id, property) as unknown as ViewData

    const tabs = propertyViewTabs()
    const [activeTab, setActiveTab] = useState(tabs[0].name)
    const [activeMaterialsTab, setActiveMaterialsTab] = useState('photos')

    useEffect(() => {
        if (fetchingPhotos || fetchingGroundPlans) return

        switch (true) {
            case Boolean(!photos?.length && !groundPlans?.length):
                if (property?.mapLatitude && property?.mapLongitude) {
                    setActiveMaterialsTab('map')
                    return
                }

                if (property?.virtualWalkUrl) {
                    setActiveMaterialsTab('3d')
                    return
                }

                if (property?.propertyVideoUrl) {
                    setActiveMaterialsTab('video')
                    return
                }
                break
            case Boolean(!photos?.length && groundPlans?.length):
                setActiveMaterialsTab('groundPlans')
                break
            default:
                setActiveMaterialsTab('photos')
                break
        }
    }, [fetchingPhotos, fetchingGroundPlans, property, photos?.length, groundPlans?.length])

    const handleTabChange = (tab: string) => {
        if (activeTab !== tab) {
            setActiveTab(tab)
        }
    }

    const handleMaterialsTabChange = (tab: string) => {
        if (activeMaterialsTab !== tab) {
            setActiveMaterialsTab(tab)
        }
    }

    const showLink = (action: string) => {
        const statuses = [PROPERTY_STATUS.ACTIVE_OFFER, PROPERTY_STATUS.FOR_AUTHORIZATION]

        const showLinkConfig: Record<string, boolean> = {
            clone: statuses.includes(property?.propertyStatus?.id as number),
            returnToOffer: !statuses.includes(property?.propertyStatus?.id as number),
        }

        return showLinkConfig[action]
    }

    const handleModal = (modalType: string) => {
        const payload = getModalPayloads(modalType, property, id, params, photos?.[0])

        if (!payload) return

        setupDeleteCallback(modalType, payload.modalProps as DeleteModalProps, (propertyId) =>
            deleteProperty({ id: propertyId as number }, { onSuccess: () => history.push('/properties') })
        )

        dispatch(showModal(payload))
    }

    if (fetchingProperty) {
        return <Loader />
    }

    const renderBuyerTitle = (propertyStatusId: number | undefined) => {
        if (propertyStatusId === 3 || propertyStatusId === 8) return t('view.property.buyer.sold.header')
        else if (propertyStatusId === 4 || propertyStatusId === 9) return t('view.property.buyer.rented.header')
        else return undefined
    }

    return (
        <EntityParamsProvider value={params}>
            <Row>
                <Col xs={12}>
                    {property && (
                        <HeaderPropertyView
                            property={property}
                            currency={currency}
                            areaUnit={areaUnit}
                            locationData={viewData?.location}
                            module={location}
                        />
                    )}
                </Col>
            </Row>

            <Row className="d-flex justify-content-between align-items-center mb-3">
                <Col sm={12} md={8}>
                    <FormTabNavigation tabs={tabs} activeTab={activeTab} handleTabChange={handleTabChange} customClass="detail-view-tabs">
                        <NavItem>
                            <UncontrolledDropdown>
                                <DropdownToggle title={t('table.showMore')} href="#" className="real-estate-form-nav-link nav-link dropdown" tag="a">
                                    <i className="ri-more-fill align-middle"></i>
                                </DropdownToggle>
                                <DropdownMenu className="dropdown-menu-end">
                                    <AppPermissionsSwitcher permission="ModulePermissions" module={location} action={action.edit}>
                                        {({ hasModulePermission }: PermissionsProps) =>
                                            hasModulePermission && (
                                                <DropdownItem onClick={() => history.push(`/properties/${id}/edit`)}>
                                                    <i className="mdi mdi-pencil-outline align-bottom me-2 text-muted"></i>{' '}
                                                    {t('view.property.action.edit')}
                                                </DropdownItem>
                                            )
                                        }
                                    </AppPermissionsSwitcher>
                                    <AppPermissionsSwitcher permission="ModulePermissions" module={location} action={action.delete}>
                                        {({ hasModulePermission }: PermissionsProps) =>
                                            hasModulePermission && (
                                                <DropdownItem onClick={() => handleModal(DELETE_MODAL)}>
                                                    <i className="mdi mdi-trash-can-outline align-bottom me-2 text-muted"></i>{' '}
                                                    {t('view.property.action.delete')}
                                                </DropdownItem>
                                            )
                                        }
                                    </AppPermissionsSwitcher>
                                    <DropdownItem onClick={() => history.push(`/properties/new`)}>
                                        <i className="mdi mdi-plus align-bottom me-2 text-muted"></i> {t('view.property.action.add')}
                                    </DropdownItem>
                                    {showLink('clone') && (
                                        <DropdownItem onClick={() => handleModal(CLONE_MODAL)}>
                                            <i className="mdi mdi-content-copy align-bottom me-2 text-muted"></i> {t('view.property.action.clone')}
                                        </DropdownItem>
                                    )}
                                    {showLink('returnToOffer') && (
                                        <DropdownItem onClick={() => handleModal(RETURN_TO_OFFER_MODAL)}>
                                            <i className="mdi mdi-undo-variant align-bottom me-2 text-muted"></i>
                                            {t('app.common.returnToOffer')}
                                        </DropdownItem>
                                    )}
                                    <DropdownItem onClick={() => handleModal(OFFER_BASE_MODAL)}>
                                        <i className="mdi mdi-file-document-multiple-outline align-bottom me-2 text-muted"></i>{' '}
                                        {t('app.common.offerOrPrint')}
                                    </DropdownItem>
                                    <DropdownItem onClick={() => handleModal(AGENT_PRINT_MODAL)}>
                                        <i className="mdi mdi-file-document-multiple-outline align-bottom me-2 text-muted"></i>{' '}
                                        {t('app.common.agentPrint')}
                                    </DropdownItem>
                                    <DropdownItem onClick={() => handleModal(TEMPLATES_MODAL)}>
                                        <i className="mdi mdi-file-document-multiple-outline align-bottom me-2 text-muted"></i>{' '}
                                        {t('table.action.shopWindowPrint')}
                                    </DropdownItem>
                                    <DropdownItem onClick={() => handleModal(ACTIVITY_MODAL)}>
                                        <i className="mdi mdi-calendar-plus align-bottom me-2 text-muted"></i>
                                        {t('app.common.addActivity')}
                                    </DropdownItem>
                                </DropdownMenu>
                            </UncontrolledDropdown>
                        </NavItem>
                    </FormTabNavigation>
                </Col>
                <Col sm={12} md={4}>
                    <NavigationActions links={propertyData?.links} />
                </Col>
            </Row>

            <TabContent activeTab={activeTab} className="text-muted">
                <TabPane tabId="about">
                    <Row>
                        <Col sm={12} xxl={3}>
                            {viewData?.general && viewData.general.some((x) => x.value) && <ViewDataCard dataList={viewData.general} />}
                            {viewData?.location && viewData.location.some((x) => x.value) && <ViewDataCard dataList={viewData.location} />}
                            {viewData?.buyer && renderBuyerTitle(property?.propertyStatus.id) && viewData.buyer.some((x) => x.value) && (
                                <CollapseCard header={renderBuyerTitle(property?.propertyStatus.id)} data={viewData.buyer}>
                                    <ViewDataList dataList={viewData.buyer} />
                                </CollapseCard>
                            )}
                            {viewData?.owners && viewData.owners.some((x) => x.value) && (
                                <CollapseCard header={t('view.property.owner.header')} data={viewData.owners}>
                                    <ViewDataList dataList={viewData.owners} />
                                </CollapseCard>
                            )}
                            {viewData?.zk && viewData.zk.some((x) => x.value) && (
                                <CollapseCard header={t('view.property.registry.header')} data={viewData.zk}>
                                    <ViewDataList dataList={viewData.zk} />
                                </CollapseCard>
                            )}
                            <ViewDataCard dataList={viewData?.other} />
                        </Col>
                        <Col sm={12} xxl={9}>
                            <Card>
                                <CardBody>
                                    <Row>
                                        <Col sm={12} xl={6}>
                                            <ViewDataList dataList={viewData?.price} />
                                            <ViewDataList dataList={viewData?.details} />
                                        </Col>
                                        <Col sm={12} xl={6}>
                                            {fetchingPhotos || fetchingGroundPlans ? (
                                                <div className="d-flex align-items-center justify-content-center" style={{ height: '370px' }}>
                                                    <Loader />
                                                </div>
                                            ) : (
                                                activeMaterialsTab && (
                                                    <Row className="mb-4">
                                                        <Col>
                                                            <TabContent activeTab={activeMaterialsTab}>
                                                                {photos?.length ? (
                                                                    <TabPane tabId="photos">
                                                                        <Slider module={location} imageList={photos} />
                                                                    </TabPane>
                                                                ) : null}
                                                                {groundPlans?.length ? (
                                                                    <TabPane tabId="groundPlans">
                                                                        <Slider module={location} imageList={groundPlans} />
                                                                    </TabPane>
                                                                ) : null}
                                                                {property?.virtualWalkUrl ? (
                                                                    <TabPane tabId="3d">
                                                                        <div className="ratio ratio-4x3">
                                                                            <iframe title="3D virtualna šetnja" src={property?.virtualWalkUrl} />
                                                                        </div>
                                                                    </TabPane>
                                                                ) : null}
                                                                {property?.propertyVideoUrl ? (
                                                                    <TabPane tabId="video">
                                                                        <div className="ratio ratio-4x3">
                                                                            <iframe
                                                                                title="Youtube video"
                                                                                src={embedYotubeVideo(property?.propertyVideoUrl)}
                                                                            />
                                                                        </div>
                                                                    </TabPane>
                                                                ) : null}
                                                                {property?.mapLatitude && property?.mapLongitude ? (
                                                                    <AppPermissionsSwitcher permission="GoogleMaps" module={location}>
                                                                        {({ showMapsSlider }: PermissionsProps) =>
                                                                            showMapsSlider ? (
                                                                                <TabPane tabId="map">
                                                                                    <MapContainer
                                                                                        marker={coerceLatLng(property)}
                                                                                        showRadius={isApproxLocationSyncType(property)}
                                                                                        height="399px"
                                                                                    />
                                                                                </TabPane>
                                                                            ) : null
                                                                        }
                                                                    </AppPermissionsSwitcher>
                                                                ) : null}
                                                            </TabContent>

                                                            <AppPermissionsSwitcher
                                                                permission="AllowedPhotos"
                                                                module={location}
                                                                photos={photos}
                                                                groundPlans={groundPlans}
                                                            >
                                                                {({ photos, groundPlans }: { photos: Image[]; groundPlans: Image[] }) =>
                                                                    photos?.length || groundPlans?.length ? (
                                                                        <SliderOptions
                                                                            photos={photos}
                                                                            groundPlans={groundPlans}
                                                                            activeMaterialsTab={activeMaterialsTab}
                                                                            handleMaterialsTabChange={handleMaterialsTabChange}
                                                                            viewData={property}
                                                                        />
                                                                    ) : null
                                                                }
                                                            </AppPermissionsSwitcher>
                                                        </Col>
                                                    </Row>
                                                )
                                            )}
                                            <ViewDataList dataList={viewData?.relatedData} />
                                        </Col>
                                    </Row>
                                    {property?.translations?.[currentLang]?.propertyTitle && (
                                        <Row className="align-items-center mt-5">
                                            <Col sm={3}>
                                                <span className="flex-grow-1 fw-medium">{t('app.common.title')}:</span>
                                            </Col>
                                            <Col sm={9}>
                                                <span className="flex-grow-1 fw-light">{property?.translations[currentLang].propertyTitle}</span>
                                            </Col>
                                        </Row>
                                    )}
                                    {property?.translations?.[currentLang]?.propertyDescription && (
                                        <Row className="mt-5">
                                            <Col sm={12} className="mb-3">
                                                <span className="flex-grow-1 fw-medium">{t('view.property.webDescription')}</span>
                                            </Col>
                                            <Col sm={12} className="fw-light text-secondary">
                                                <div
                                                    dangerouslySetInnerHTML={{
                                                        __html: property.translations[currentLang].propertyDescription || '',
                                                    }}
                                                />
                                            </Col>
                                        </Row>
                                    )}
                                    {property?.propertyPortal?.length || property?.propertyWebsite?.length ? (
                                        <Row className="mt-5">
                                            {property?.propertyPortal?.length ? (
                                                <Col sm={6}>
                                                    <h4 className="fw-medium fs-13 mb-4">{t('view.property.webPortalAdvertised')}</h4>
                                                    {property.propertyPortal.map((item) => (
                                                        <div key={item.id} className="mb-2 fs-13 fw-medium">
                                                            {item.title}
                                                        </div>
                                                    ))}
                                                </Col>
                                            ) : null}
                                            {property?.propertyWebsite?.length ? (
                                                <Col sm={6}>
                                                    <h4 className="fw-medium fs-13 mb-4">{t('view.property.webPageAdvertised')}</h4>
                                                    {property.propertyWebsite.map((item) => (
                                                        <div key={item.id} className="mb-4 fs-13">
                                                            <span className="fw-medium d-block mb-3">{item.website.title}</span>
                                                            {WEBSITE_ATTRIBUTES.map(({ attribute, name }) => {
                                                                return (
                                                                    <Row key={attribute} className=" mb-2">
                                                                        <Col sm={6}>
                                                                            <span className="flex-grow-1 fw-light">{t(name)}:</span>
                                                                        </Col>
                                                                        <Col sm={6}>
                                                                            <span className="flex-grow-1 fw-light">
                                                                                {item?.[attribute as keyof PropertyWebsite]
                                                                                    ? t('view.common.conclusion.yes')
                                                                                    : t('view.common.conclusion.no')}
                                                                            </span>
                                                                        </Col>
                                                                    </Row>
                                                                )
                                                            })}
                                                        </div>
                                                    ))}
                                                </Col>
                                            ) : null}
                                        </Row>
                                    ) : null}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </TabPane>
                {activeTab === 'documents' && (
                    <TabPane tabId="documents">
                        <Card>
                            <CardBody>
                                <DocumentTable
                                    parentId={id}
                                    documentModule="property"
                                    onAddClick={() => {
                                        history.push({
                                            pathname: `/properties/${id}/edit`,
                                            state: { activeTab: 'documents' },
                                        })
                                    }}
                                />
                            </CardBody>
                        </Card>
                    </TabPane>
                )}
                {activeTab === 'notes' && (
                    <TabPane tabId="notes">
                        <NotesViewAccordion entityId={id} noteEntity={'property'} />
                    </TabPane>
                )}
                {activeTab === 'opportunity' && (
                    <TabPane tabId="opportunity">
                        <OpportunityList />
                    </TabPane>
                )}
            </TabContent>
        </EntityParamsProvider>
    )
}
